<div class="purchase-type-editor" *ngIf="purchaseType">
  <div class="editor-header">
    <div class="editor-title">
      <h1>{{isNew ? 'New Purchase Type' : purchaseType.name}}</h1>
      <span class="key-badge" *ngIf="purchaseType.key">{{purchaseType.key}}</span>
      <span class="inactive-badge" *ngIf="purchaseType.active === false">Inactive</span>
    </div>
    <div class="editor-actions">
      <button class="button red" *ngIf="!isNew && purchaseType.active !== false" (click)="deactivate()">Deactivate</button>
      <button class="button" [disabled]="saving" (click)="save()">{{isNew ? 'Create Purchase Type' : 'Save Purchase Type'}}</button>
      <button class="button" (click)="close()">Close</button>
    </div>
  </div>

  <div class="editor-body">

    <h3 class="section-title">General</h3>
    <div class="form-grid">
      <label>
        Name
        <input type="text" placeholder="purchase type name" [(ngModel)]="purchaseType.name" />
      </label>
      <label>
        Key
        <input type="text" placeholder="auto-generated when blank" [(ngModel)]="purchaseType.key" />
      </label>
      <label>
        Transfer Key Prefix
        <input type="text" placeholder="e.g. W" [(ngModel)]="purchaseType.keyPrefix" />
      </label>
      <label>
        Source Type
        <select [(ngModel)]="purchaseType.sourceType">
          <option *ngFor="let locationType of locationTypes" [value]="locationType">{{locationType === 'retail,warehouse' ? 'retail or warehouse' : (locationType || '(none)')}}</option>
        </select>
      </label>
      <label>
        Source Label
        <input type="text" placeholder="e.g. From Warehouse" [(ngModel)]="purchaseType.sourceLabel" />
      </label>
      <label>
        Target Type
        <select [(ngModel)]="purchaseType.targetType">
          <option *ngFor="let locationType of locationTypes" [value]="locationType">{{locationType === 'retail,warehouse' ? 'retail or warehouse' : (locationType || '(none)')}}</option>
        </select>
      </label>
      <label>
        Target Label
        <input type="text" placeholder="e.g. To Store" [(ngModel)]="purchaseType.targetLabel" />
      </label>
      <label>
        Build Button Label
        <input type="text" placeholder="Build Transfer" [(ngModel)]="purchaseType.buildLabel" />
      </label>
      <label>
        Auth Context
        <select [(ngModel)]="purchaseType.authContext">
          <option *ngFor="let context of authContexts" [value]="context.key">{{context.name}}</option>
        </select>
      </label>
      <label>
        Create Auth Context
        <select [(ngModel)]="purchaseType.createAuthContext">
          <option *ngFor="let context of createAuthContexts" [value]="context.key">{{context.name}}</option>
        </select>
      </label>
      <label>
        Add Upon Scan Qty
        <input type="number" placeholder="blank = select only" [(ngModel)]="purchaseType.addUponScan" />
      </label>
      <label>
        Re-Scan Adds Qty
        <input type="number" placeholder="blank = select only" [(ngModel)]="purchaseType.rescanAddQty" />
      </label>
      <label>
        Item View Key
        <input type="text" placeholder="e.g. retail-product" [(ngModel)]="purchaseType.itemViewKey" />
      </label>
      <label>
        Item Report Key
        <input type="text" placeholder="e.g. retail-products" [(ngModel)]="purchaseType.itemReportKey" />
      </label>
      <label>
        Import Quantity Means
        <select [ngModel]="purchaseType.importQtySemantics || ''" (ngModelChange)="purchaseType.importQtySemantics = $event">
          <option *ngFor="let option of importQtySemanticsOptions" [value]="option.key">{{option.name}}</option>
        </select>
      </label>
    </div>
    <div class="hint">Item View Key: users authorized on that View can click a transfer line's SKU/Item cell (desktop) to open the item in a side panel. Item Report Key names the report that view belongs to.</div>
    <div class="hint">Create Auth Context: which side's permission gates CREATING a transfer of this type (e.g. customerId lets stores initiate warehouse orders). Working the first step still follows that step's own context.</div>

    <div class="flag-row">
      <label class="flag">
        <app-toggle [checked]="purchaseType.active !== false" (valueChanged)="purchaseType.active = $event"></app-toggle>
        Active
      </label>
      <label class="flag">
        <app-toggle [checked]="purchaseType.allowAddAll === true" (valueChanged)="purchaseType.allowAddAll = $event"></app-toggle>
        Allow Add All
      </label>
      <label class="flag" title="Hide from the transfers screen's type dropdown — transfers of this type are created only via a report's add button (deep link).">
        <app-toggle [checked]="purchaseType.unlisted === true" (valueChanged)="purchaseType.unlisted = $event"></app-toggle>
        Unlisted (create from report only)
      </label>
      <label class="flag" title="Break Case: lines are case items resolved through the single's Replenish From link (scanning either side works); pair with a step whose inventory flag is Break Case.">
        <app-toggle [checked]="purchaseType.breakCase === true" (valueChanged)="purchaseType.breakCase = $event"></app-toggle>
        Break Case (resolve lines to the case)
      </label>
      <label class="flag" title="Count-as-of: shows the Inventory Count Date/Time field and replays movement posted after it so displayed balances match the count moment.">
        <app-toggle [checked]="purchaseType.countAsOf === true" (valueChanged)="purchaseType.countAsOf = $event"></app-toggle>
        Count As-Of Date
      </label>
      <label class="flag" *ngIf="purchaseType.countAsOf === true" title="Each counted line takes its On Hand basis from the balance at the moment it is counted (a queued offline scan: the device time of the scan), so sales rung between the count's start and that product's shelf count are not deducted twice at completion. Uncounted lines keep the Count Date/Time basis.">
        <app-toggle [checked]="purchaseType.countAsOfLine === true" (valueChanged)="purchaseType.countAsOfLine = $event"></app-toggle>
        Count As-Of Per Line (basis when counted)
      </label>
      <label class="flag">
        <app-toggle [checked]="purchaseType.defaultPendingQty === true" (valueChanged)="purchaseType.defaultPendingQty = $event"></app-toggle>
        Default Pending Qty
      </label>
      <label class="flag">
        <app-toggle [checked]="purchaseType.requirePendingCount === true" (valueChanged)="purchaseType.requirePendingCount = $event"></app-toggle>
        Require Pending Count
      </label>
      <label class="flag">
        <app-toggle [checked]="purchaseType.hideCostTotal === true" (valueChanged)="purchaseType.hideCostTotal = $event"></app-toggle>
        Hide Cost Total
      </label>
      <label class="flag">
        <app-toggle [checked]="purchaseType.wholeVendorUnits === true" (valueChanged)="purchaseType.wholeVendorUnits = $event"></app-toggle>
        Whole Vendor Units (round unit entries up to full cases)
      </label>
      <label class="flag">
        <app-toggle [checked]="purchaseType.defaultCostFromPrice === true" (valueChanged)="purchaseType.defaultCostFromPrice = $event"></app-toggle>
        Default Cost From Price
      </label>
      <label class="flag">
        <app-toggle [checked]="purchaseType.allowNegativeQty !== false" (valueChanged)="purchaseType.allowNegativeQty = $event"></app-toggle>
        Allow Negative Qty
      </label>
      <label class="flag" *ngIf="purchaseType.allowNegativeQty === false">
        <app-toggle [checked]="purchaseType.invertNegativeQty === true" (valueChanged)="purchaseType.invertNegativeQty = $event"></app-toggle>
        Invert Negative Qty
      </label>
    </div>

    <h3 class="section-title">Inventory Availability</h3>
    <div class="hint">Which sides an inventory Account must be assigned to before it can be added to this transfer.</div>
    <div class="flag-row">
      <label class="flag" *ngFor="let key of requiredLocationKeyOptions">
        <app-toggle [checked]="hasRequiredLocationKey(key)" (valueChanged)="toggleRequiredLocationKey(key)"></app-toggle>
        Require assignment at {{key}}
      </label>
    </div>

    <h3 class="section-title">Transit &amp; Discrepancy Plumbing</h3>
    <div class="hint">Resolved automatically at movement time; pin a specific location only when needed.</div>
    <div class="form-grid">
      <label>
        Transit Location Type
        <input type="text" placeholder="default: transit" [(ngModel)]="purchaseType.transit.locationType" />
      </label>
      <label>
        Transit Location Id (pin)
        <input type="text" placeholder="optional ObjectId" [(ngModel)]="purchaseType.transit.locationId" />
      </label>
      <label>
        Discrepancy Location Type
        <input type="text" placeholder="default: discrepancy" [(ngModel)]="purchaseType.discrepancy.locationType" />
      </label>
      <label>
        Discrepancy Location Id (pin)
        <input type="text" placeholder="optional ObjectId" [(ngModel)]="purchaseType.discrepancy.locationId" />
      </label>
    </div>

    <div class="section-header">
      <h3 class="section-title">Custom Fields</h3>
      <button class="button small" (click)="addCustomField()">Add Custom Field</button>
    </div>
    <div class="row-list" *ngIf="purchaseType.customFields.length > 0">
      <div class="row-item" *ngFor="let field of purchaseType.customFields; let fieldIndex = index">
        <input type="text" placeholder="name" [(ngModel)]="field.name" />
        <input type="text" placeholder="key" [(ngModel)]="field.key" />
        <label class="flag"><input type="checkbox" [(ngModel)]="field.required" title="Must be filled in when the transfer is created" /> Required</label>
        <button class="button small red" (click)="removeCustomField(fieldIndex)">&times;</button>
      </div>
    </div>

    <div class="section-header">
      <h3 class="section-title">Subtypes</h3>
      <button class="button small" (click)="addSubtype()">Add Subtype</button>
    </div>
    <div class="row-list" *ngIf="purchaseType.subtypes.length > 0">
      <div class="row-item" *ngFor="let subtype of purchaseType.subtypes; let subtypeIndex = index">
        <input type="text" placeholder="name" [(ngModel)]="subtype.name" />
        <input type="text" placeholder="key" [(ngModel)]="subtype.key" />
        <button class="button small red" (click)="removeSubtype(subtypeIndex)">&times;</button>
      </div>
    </div>

    <div class="section-header">
      <h3 class="section-title">Steps ({{purchaseType.steps.length}})</h3>
      <button class="button small" (click)="addStep()">Add Step</button>
    </div>
    <div class="hint" *ngIf="purchaseType.steps.length === 0">
      No steps — this type runs the legacy open &rarr; pending &rarr; complete flow.
    </div>

    <div class="step" *ngFor="let step of purchaseType.steps; let stepIndex = index">
      <div class="step-header" (click)="step.collapsed = !step.collapsed">
        <div class="step-swatch" [style.background]="step.color || '#888'"></div>
        <div class="step-header-name">{{stepIndex + 1}}. {{step.name || '(unnamed step)'}}</div>
        <div class="step-header-key">{{step.key}}</div>
        <div class="step-header-tags">
          <span *ngIf="step.allowAddRemove">add/remove</span>
          <span *ngIf="step.requireCount">count</span>
          <span *ngIf="step.requireReconcile">reconcile</span>
          <span *ngIf="step.inventory.removeFromSource">&minus;source</span>
          <span *ngIf="step.inventory.addToTarget">+target</span>
        </div>
        <div class="step-controls" (click)="$event.stopPropagation()">
          <button class="button small" [disabled]="stepIndex === 0" (click)="moveStep(stepIndex, -1)">&uarr;</button>
          <button class="button small" [disabled]="stepIndex === purchaseType.steps.length - 1" (click)="moveStep(stepIndex, 1)">&darr;</button>
          <button class="button small red" (click)="removeStep(stepIndex)">&times;</button>
        </div>
      </div>

      <div class="step-body" *ngIf="!step.collapsed">
        <div class="form-grid">
          <label>
            Step Key
            <input type="text" placeholder="e.g. building" [(ngModel)]="step.key" />
          </label>
          <label>
            Step Name
            <input type="text" placeholder="e.g. Building" [(ngModel)]="step.name" />
          </label>
          <label>
            Color
            <input type="text" placeholder="#4488cc" [(ngModel)]="step.color" />
          </label>
          <label>
            Auth Context
            <select [(ngModel)]="step.authContext">
              <option *ngFor="let context of stepAuthContexts" [value]="context.key">{{context.name}}</option>
            </select>
          </label>
          <label>
            Permission Action
            <input type="text" placeholder="defaults to step key" [(ngModel)]="step.permissionAction" />
          </label>
          <label>
            Proceed Permission Action
            <input type="text" placeholder="defaults to step permission" [(ngModel)]="step.proceedPermissionAction" />
          </label>
          <label>
            Proceed Label
            <input type="text" placeholder="defaults to next step name" [(ngModel)]="step.proceedLabel" />
          </label>
          <label>
            Regress Label
            <input type="text" placeholder="defaults to 'Back to X'" [(ngModel)]="step.regressLabel" />
          </label>
          <label class="wide">
            Confirm Proceed Message
            <input type="text" placeholder="blank = no confirmation prompt" [(ngModel)]="step.confirmProceed" />
          </label>
          <label class="wide">
            Confirm Step-Back Message
            <input type="text" placeholder="blank = no confirmation prompt when going back to the previous step" [(ngModel)]="step.confirmRegress" />
          </label>
        </div>

        <div class="flag-row">
          <label class="flag">
            <app-toggle [checked]="step.allowAddRemove === true" (valueChanged)="step.allowAddRemove = $event"></app-toggle>
            Allow Add/Remove Items
          </label>
          <label class="flag" title="Scans of items not on the transfer may be received as UNEXPECTED lines (Expected 0, flagged as discrepancies) even when Add/Remove is off.">
            <app-toggle [checked]="step.allowUnexpectedAdd === true" (valueChanged)="step.allowUnexpectedAdd = $event"></app-toggle>
            Allow Unexpected Items
          </label>
          <label class="flag">
            <app-toggle [checked]="step.allowRegress !== false" (valueChanged)="step.allowRegress = $event"></app-toggle>
            Allow Regress
          </label>
          <label class="flag">
            <app-toggle [checked]="step.requireCount === true" (valueChanged)="step.requireCount = $event"></app-toggle>
            Require Count
          </label>
          <label class="flag">
            <app-toggle [checked]="step.requireReconcile === true" (valueChanged)="step.requireReconcile = $event"></app-toggle>
            Require Reconcile
          </label>
          <label class="flag">
            <app-toggle [checked]="step.inventory.removeFromSource === true" (valueChanged)="step.inventory.removeFromSource = $event"></app-toggle>
            Inventory: Remove From Source
          </label>
          <label class="flag">
            <app-toggle [checked]="step.inventory.addToTarget === true" (valueChanged)="step.inventory.addToTarget = $event"></app-toggle>
            Inventory: Add To Target
          </label>
          <label class="flag" title="Break Case completion: each line's cases leave the target's stock and cases × singles-per-case of the linked single are added there.">
            <app-toggle [checked]="step.inventory.breakCase === true" (valueChanged)="step.inventory.breakCase = $event"></app-toggle>
            Inventory: Break Case (cases out, linked singles in)
          </label>
        </div>

        <div class="section-header sub">
          <h4>Columns</h4>
          <button class="button small" (click)="addStepField(step)">Add Column</button>
        </div>
        <div class="row-list">
          <div class="row-item header" *ngIf="step.fields.length > 0">
            <div>Name</div>
            <div>Key</div>
            <div>Type</div>
            <div>Class</div>
            <div class="narrow">Editable</div>
            <div class="narrow">Mobile</div>
            <div class="narrow">Required</div>
            <div>Default</div>
            <div>Units Of</div>
            <div class="actions"></div>
          </div>
          <div class="row-item" *ngFor="let field of step.fields; let fieldIndex = index">
            <input type="text" placeholder="name" [(ngModel)]="field.name" />
            <input type="text" placeholder="key" [(ngModel)]="field.key" />
            <select [(ngModel)]="field.type">
              <option *ngFor="let fieldType of fieldTypes" [value]="fieldType">{{fieldType || '(none)'}}</option>
            </select>
            <select [(ngModel)]="field.class">
              <option *ngFor="let fieldClass of fieldClasses" [value]="fieldClass">{{fieldClass || '(none)'}}</option>
            </select>
            <input class="narrow" type="checkbox" [(ngModel)]="field.editable" title="Editable" />
            <input class="narrow" type="checkbox" [(ngModel)]="field.displayMobile" title="Display on mobile" />
            <input class="narrow" type="checkbox" [(ngModel)]="field.required" title="Every line must have a value before the step can proceed" />
            <input type="text" placeholder="true / qty" [(ngModel)]="field.default" />
            <input type="text" placeholder="e.g. qty" title="Retail-unit view of this vendor-unit field (base key)" [(ngModel)]="field.unitOf" />
            <button class="button small red" (click)="removeStepField(step, fieldIndex)">&times;</button>
          </div>
        </div>

        <div class="section-header sub">
          <h4>Info Fields</h4>
          <button class="button small" (click)="addInfoField(step)">Add Info Field</button>
        </div>
        <div class="row-list">
          <div class="row-item" *ngFor="let field of step.infoFields; let fieldIndex = index">
            <input type="text" placeholder="name" [(ngModel)]="field.name" />
            <input type="text" placeholder="account field key" [(ngModel)]="field.key" />
            <select [(ngModel)]="field.type">
              <option *ngFor="let fieldType of fieldTypes" [value]="fieldType">{{fieldType || '(none)'}}</option>
            </select>
            <button class="button small red" (click)="removeInfoField(step, fieldIndex)">&times;</button>
          </div>
        </div>

        <ng-container *ngFor="let opsKey of ['onEnter', 'onRegress']">
          <div class="section-header sub">
            <h4>{{opsKey === 'onEnter' ? 'On Enter Ops' : 'On Regress Ops'}}</h4>
            <button class="button small" (click)="addOp(step, opsKey)">Add Op</button>
          </div>
          <div class="row-list">
            <div class="row-item" *ngFor="let op of step[opsKey]; let opIndex = index">
              <select [(ngModel)]="op.op">
                <option *ngFor="let opType of opTypes" [value]="opType">{{opType}}</option>
              </select>
              <ng-container *ngIf="op.op === 'copyItemField'">
                <input type="text" placeholder="from field" [(ngModel)]="op.from" />
                <input type="text" placeholder="to field" [(ngModel)]="op.to" />
              </ng-container>
              <ng-container *ngIf="op.op === 'setItemField'">
                <input type="text" placeholder="field key" [(ngModel)]="op.key" />
                <input type="text" placeholder="value" [(ngModel)]="op.value" />
              </ng-container>
              <ng-container *ngIf="op.op === 'clearItemField' || op.op === 'sortItemsBy'">
                <input type="text" placeholder="field key" [(ngModel)]="op.key" />
              </ng-container>
              <button class="button small red" (click)="removeOp(step, opsKey, opIndex)">&times;</button>
            </div>
          </div>
        </ng-container>

      </div>
    </div>

  </div>
</div>
